import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { CheckCircle, Clock, Phone, Mail, ArrowRight } from "lucide-react";
import logo from "../public/logo.png";

export function Success() {
  const [firstName, setFirstName] = useState<string>("");

  useEffect(() => {
    // Get the applicant's first name from sessionStorage
    const storedName = sessionStorage.getItem("applicantFirstName");
    if (storedName) {
      setFirstName(storedName);
      // Clear it after retrieving
      sessionStorage.removeItem("applicantFirstName");
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      {/* Header */}
      <header className="px-5 md:px-10 py-5 border-b border-white/10">
        <div className="max-w-4xl mx-auto flex items-center justify-center">
          <a href="/">
            <img src={logo} alt="Empire Insurance" className="h-10 md:h-12" />
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-lg text-center"
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-10 h-10 text-emerald-400" />
          </motion.div>

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-3xl md:text-4xl font-extrabold text-white mb-4"
          >
            You're In{firstName ? `, ${firstName}` : ""}!
          </motion.h1>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-lg text-gray-400 mb-8"
          >
            Your application has been received. We're excited to learn more about you.
          </motion.p>

          {/* What's Next Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 mb-8 text-left"
          >
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Clock className="w-5 h-5 text-emerald-400" />
              What Happens Next
            </h2>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-emerald-400 font-bold text-sm">1</span>
                </div>
                <div>
                  <p className="text-white font-semibold">Application Review</p>
                  <p className="text-gray-500 text-sm">Our team will review your application within 24 hours.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-emerald-400 font-bold text-sm">2</span>
                </div>
                <div>
                  <p className="text-white font-semibold">Phone Screening</p>
                  <p className="text-gray-500 text-sm">If we decide to move forward, we'll call you to discuss the opportunity.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-emerald-400 font-bold text-sm">3</span>
                </div>
                <div>
                  <p className="text-white font-semibold">Get Started</p>
                  <p className="text-gray-500 text-sm">Start training and begin your journey to earning uncapped commissions.</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-gray-500 mb-8"
          >
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <span>We'll contact you soon</span>
            </div>
            <span className="hidden sm:block">|</span>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span>Check your email for updates</span>
            </div>
          </motion.div>

          {/* Back to Home */}
          <motion.a
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            href="/"
            className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-semibold transition-colors"
          >
            Back to Home <ArrowRight className="w-4 h-4" />
          </motion.a>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="px-5 md:px-10 lg:px-16 py-10 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
            <img src={logo} alt="Empire Insurance" className="h-10" />
            <div className="flex items-center gap-6">
              <a href="/privacy" className="text-sm text-gray-500 hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="/terms" className="text-sm text-gray-500 hover:text-white transition-colors">
                Terms of Use
              </a>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 space-y-4">
            <p className="text-xs text-gray-600 leading-relaxed text-center">
              This site is not a part of the Facebook™ website or Facebook™ Inc. Additionally, this site is NOT endorsed by Facebook™ in any way. FACEBOOK™ is a trademark of FACEBOOK™, Inc. Meta™ and the Meta™ logo are trademarks of Meta Platforms, Inc.
            </p>
            <p className="text-xs text-gray-600 leading-relaxed text-center">
              The income figures stated ($8K-$15K/month) represent earnings of top-performing agents and are not typical or guaranteed. Your results will vary and depend on many factors including but not limited to your background, experience, work ethic, and market conditions. All business entails risk and requires consistent effort and action. This is a commission-only position with no guaranteed base salary.
            </p>
            <p className="text-sm text-gray-600 text-center">
              © {new Date().getFullYear()} Empire Insurance Agency. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
